import toast from 'react-hot-toast';

/**
 * Centralized toast notification system
 * Replace all alert() calls with these functions
 */

/**
 * Show success toast
 * @param {string} message - Success message to display
 * @param {Object} options - Additional toast options
 */
export const showSuccess = (message, options = {}) => {
    return toast.success(message, {
        duration: 4000,
        position: 'top-center',
        style: {
            background: '#4CAF50',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
        ...options,
    });
};

/**
 * Show error toast
 * @param {string} message - Error message to display
 * @param {Object} options - Additional toast options
 */
export const showError = (message, options = {}) => {
    return toast.error(message, {
        duration: 5000,
        position: 'top-center',
        style: {
            background: '#f44336',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
        ...options,
    });
};

/**
 * Show info toast
 * @param {string} message - Info message to display
 * @param {Object} options - Additional toast options
 */
export const showInfo = (message, options = {}) => {
    return toast(message, {
        duration: 4000,
        position: 'top-center',
        icon: 'ℹ️',
        style: {
            background: '#2196F3',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
        ...options,
    });
};

/**
 * Show warning toast
 * @param {string} message - Warning message to display
 * @param {Object} options - Additional toast options
 */
export const showWarning = (message, options = {}) => {
    return toast(message, {
        duration: 4000,
        position: 'top-center',
        icon: '⚠️',
        style: {
            background: '#FF9800',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
        ...options,
    });
};

/**
 * Show loading toast (returns a toast ID that can be used to dismiss/update)
 * @param {string} message - Loading message to display
 * @param {Object} options - Additional toast options
 * @returns {string} Toast ID
 */
export const showLoading = (message = 'Loading...', options = {}) => {
    return toast.loading(message, {
        position: 'top-center',
        style: {
            background: '#2196F3',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
        ...options,
    });
};

/**
 * Dismiss a specific toast by ID
 * @param {string} toastId - Toast ID to dismiss
 */
export const dismissToast = (toastId) => {
    toast.dismiss(toastId);
};

/**
 * Update an existing toast (useful for loading -> success/error transitions)
 * @param {string} toastId - Toast ID to update
 * @param {string} message - New message
 * @param {string} type - New type: 'success', 'error', 'info', 'warning'
 */
export const updateToast = (toastId, message, type = 'success') => {
    const config = {
        duration: type === 'error' ? 5000 : 4000,
        style: {
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
        },
    };

    switch (type) {
        case 'success':
            config.style.background = '#4CAF50';
            config.style.color = '#fff';
            toast.success(message, { id: toastId, ...config });
            break;
        case 'error':
            config.style.background = '#f44336';
            config.style.color = '#fff';
            toast.error(message, { id: toastId, ...config });
            break;
        case 'info':
            config.style.background = '#2196F3';
            config.style.color = '#fff';
            toast(message, { id: toastId, icon: 'ℹ️', ...config });
            break;
        case 'warning':
            config.style.background = '#FF9800';
            config.style.color = '#fff';
            toast(message, { id: toastId, icon: '⚠️', ...config });
            break;
        default:
            toast.success(message, { id: toastId, ...config });
    }
};


